import React, { useEffect, useState } from "react";
import "./PackageApp.css";
import { useDispatch } from "react-redux";
import { ApiPaths } from "../../Config/ApiPath";
import useAxiosHelper from "../../Common/AxiosHelper";
import { setUserPersonalInfo } from "../../Redux/ProfileDataSlice";
import { setIncomeWallet } from "../../Redux/IncomeWallet";
import { toastFailed, toastSuccess } from "../../Config/BasicInfo";
import ArrayToObject from "../../Common/ArrayToObject";
import { useNavigate } from "react-router-dom";
import {
  Wallet,
  Check,
  ShieldCheck,
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  Minus,
  Plus,
  Sparkles,
  History,
  ShoppingBag,
  X,
} from "lucide-react";
import OrderHistory from "./Order";

/* Single package — no get-all-package call.
   Must match the backend plan (plansinfos → packageId 1). */
const PACKAGE_ID = 1;
const PACKAGE_NAME = "Aethrx Package";
const MIN_AMOUNT = 100;
const STEP = 100;
const ROI_MIN = 15; // %
const ROI_MAX = 20; // %
const QUICK_AMOUNTS = [100, 500, 1000, 5000];

const money = (n, dp = 2) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;

const Packages = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { AxiosGet, AxiosPost } = useAxiosHelper();

  const [activeTab, setActiveTab] = useState("buy");
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const [username, setUsername] = useState("");
  const [fundBalance, setFundBalance] = useState(0);
  const [amountInput, setAmountInput] = useState(String(MIN_AMOUNT));

  const amount = Number(amountInput);
  const amountValid =
    amountInput !== "" && Number.isInteger(amount) && amount >= MIN_AMOUNT && amount % STEP === 0;
  const amountError =
    amountInput === ""
      ? `Enter an amount — minimum ${money(MIN_AMOUNT, 0)}`
      : !Number.isInteger(amount)
      ? "Enter a whole-dollar amount"
      : amount < MIN_AMOUNT
      ? `Minimum amount is ${money(MIN_AMOUNT, 0)}`
      : amount % STEP !== 0
      ? `Amount must be a multiple of ${money(STEP, 0)}`
      : "";
  // nearest valid amount, offered as a one-tap fix
  const suggestion =
    amountInput !== "" && Number.isFinite(amount) && amount > 0 && !amountValid
      ? Math.max(MIN_AMOUNT, Math.round(amount / STEP) * STEP)
      : null;

  const fund = Number(fundBalance) || 0;
  const maxAffordable = Math.floor(fund / STEP) * STEP;
  const insufficientFunds = amountValid && fund < amount;
  const canBuy = amountValid && !insufficientFunds;
  const balanceAfter = Math.max(0, fund - (amountValid ? amount : 0));
  const roiLow = amountValid ? (amount * ROI_MIN) / 100 : 0;
  const roiHigh = amountValid ? (amount * ROI_MAX) / 100 : 0;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // confirmation sheet: Escape closes, page behind stays put
  useEffect(() => {
    if (!showConfirm) return undefined;
    const onKey = (e) => e.key === "Escape" && !submitting && setShowConfirm(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [showConfirm, submitting]);

  async function fetchData() {
    try {
      const [profile, wallets] = await Promise.all([
        AxiosGet(ApiPaths.getProfile),
        AxiosGet(ApiPaths.getWallets),
      ]);

      if (profile) {
        dispatch(setUserPersonalInfo(profile));
        setUsername(profile.username || "");
      }

      if (wallets?.wallets) {
        dispatch(setIncomeWallet(wallets.wallets));
        const w = ArrayToObject(wallets.wallets);
        setFundBalance(w?.fund_wallet?.value || 0);
      }
    } catch (error) {
      toastFailed("Failed to load your wallet");
    } finally {
      setInitialLoading(false);
    }
  }

  const stepBy = (dir) => {
    const base = amountValid ? amount : Math.max(MIN_AMOUNT, Math.round((amount || 0) / STEP) * STEP);
    const next = Math.max(MIN_AMOUNT, base + dir * STEP);
    setAmountInput(String(next));
  };

  async function confirmPurchase() {
    if (submitting || !canBuy) return;
    try {
      setSubmitting(true);
      const body = {
        amount,
        username,
        packageId: PACKAGE_ID,
        wallet_name: "fund_wallet",
      };
      const res = await AxiosPost(ApiPaths.topUp, body);

      if (res?.message) {
        toastSuccess(res.message || "Package purchased successfully");
        setShowConfirm(false);
        await fetchData();
        setHistoryKey((k) => k + 1);
        setActiveTab("history");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toastFailed(res?.message || "Something went wrong");
      }
    } catch (e) {
      toastFailed(e?.response?.data?.message || e?.message || "Purchase failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="pkPage">
      {/* ---------- Tabs ---------- */}
      <div className="pkTabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "buy"}
          className={`pkTab ${activeTab === "buy" ? "isOn" : ""}`}
          onClick={() => setActiveTab("buy")}
        >
          <ShoppingBag size={16} /> Buy Package
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          className={`pkTab ${activeTab === "history" ? "isOn" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          <History size={16} /> My Packages
        </button>
      </div>

      {activeTab === "buy" &&
        (initialLoading ? (
          <div className="pkSkel">
            <span className="h1" />
            <span className="h2" />
            <span className="h3" />
          </div>
        ) : (
          <div className="pkBuy">
            {/* ---------- Package hero ---------- */}
            <div className="pkHero">
              <div className="pkHeroTop">
                <span className="pkBadge">
                  <ShieldCheck size={13} /> {PACKAGE_NAME}
                </span>
                <span className="pkRoiBadge">
                  <TrendingUp size={14} /> {ROI_MIN}–{ROI_MAX}% ROI
                </span>
              </div>

              <div className="pkAmountLabel">Investment amount</div>
              <div className="pkStepper">
                <button
                  type="button"
                  className="pkStepBtn"
                  onClick={() => stepBy(-1)}
                  disabled={amountValid && amount <= MIN_AMOUNT}
                  aria-label={`Decrease by ${STEP}`}
                >
                  <Minus size={20} />
                </button>
                <label className={`pkAmountField ${amountError ? "isError" : ""}`}>
                  <span>$</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={MIN_AMOUNT}
                    step={STEP}
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value.replace(/[^\d]/g, ""))}
                    aria-label="Investment amount in dollars"
                  />
                </label>
                <button
                  type="button"
                  className="pkStepBtn"
                  onClick={() => stepBy(1)}
                  aria-label={`Increase by ${STEP}`}
                >
                  <Plus size={20} />
                </button>
              </div>

              {amountError ? (
                <p className="pkAmtMsg error">
                  {amountError}
                  {suggestion && suggestion !== amount && (
                    <button type="button" onClick={() => setAmountInput(String(suggestion))}>
                      Use {money(suggestion, 0)}
                    </button>
                  )}
                </p>
              ) : (
                <p className="pkAmtMsg">
                  Minimum {money(MIN_AMOUNT, 0)} · in multiples of {money(STEP, 0)}
                </p>
              )}

              <div className="pkChips">
                {QUICK_AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    className={`pkChip ${amount === a ? "isOn" : ""}`}
                    onClick={() => setAmountInput(String(a))}
                  >
                    {money(a, 0)}
                  </button>
                ))}
                {maxAffordable >= MIN_AMOUNT && !QUICK_AMOUNTS.includes(maxAffordable) && (
                  <button
                    type="button"
                    className={`pkChip max ${amount === maxAffordable ? "isOn" : ""}`}
                    onClick={() => setAmountInput(String(maxAffordable))}
                  >
                    Max {money(maxAffordable, 0)}
                  </button>
                )}
              </div>
            </div>

            {/* ---------- ROI estimate ---------- */}
            <div className="pkRoi">
              <span className="pkRoiIcon">
                <Sparkles size={20} />
              </span>
              <div className="pkRoiText">
                <small>Estimated ROI income</small>
                <b>
                  {amountValid ? `${money(roiLow)} – ${money(roiHigh)}` : "—"}
                </b>
                <span>
                  {ROI_MIN}% to {ROI_MAX}% on {amountValid ? money(amount, 0) : "your amount"}
                </span>
              </div>
            </div>

            {/* ---------- Pay from ---------- */}
            <div className={`pkWallet ${insufficientFunds ? "isShort" : ""}`}>
              <div className="pkWalletRow">
                <span className="pkWalletIcon">
                  <Wallet size={18} />
                </span>
                <span className="pkWalletText">
                  <small>Pay from</small>
                  <b>Fund Wallet</b>
                </span>
                <span className="pkWalletBal">
                  <small>Available</small>
                  <b>{money(fund)}</b>
                </span>
              </div>
              <div className="pkWalletAfter">
                <span>Balance after purchase</span>
                <b>{insufficientFunds ? "—" : money(balanceAfter)}</b>
              </div>
              {insufficientFunds && (
                <div className="pkWarn">
                  <AlertTriangle size={15} />
                  <span>
                    You need {money(amount - fund)} more in your Fund Wallet.
                  </span>
                </div>
              )}
            </div>

            {/* ---------- Benefits ---------- */}
            <ul className="pkBenefits">
              <li>
                <i>
                  <Check size={12} />
                </i>
                <span>Earn <b>{ROI_MIN}% – {ROI_MAX}% ROI income</b> on your package</span>
              </li>
              <li>
                <i>
                  <Check size={12} />
                </i>
                <span>Start from <b>{money(MIN_AMOUNT, 0)}</b>, scale up in steps of {money(STEP, 0)}</span>
              </li>
              <li>
                <i>
                  <Check size={12} />
                </i>
                <span>Paid instantly from your <b>Fund Wallet</b></span>
              </li>
              <li>
                <i>
                  <Check size={12} />
                </i>
                <span>Track every package and its earnings in <b>My Packages</b></span>
              </li>
            </ul>

            {/* ---------- CTA (sticky on phones) ---------- */}
            <div className="pkCtaBar">
              {insufficientFunds ? (
                <button type="button" className="pkCta ghost" onClick={() => navigate("/deposit")}>
                  Add funds <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  className="pkCta"
                  disabled={!canBuy}
                  onClick={() => setShowConfirm(true)}
                >
                  Buy for {amountValid ? money(amount, 0) : "—"} <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
        ))}

      {activeTab === "history" && <OrderHistory updateOrderList={historyKey} />}

      {/* ---------- Confirm sheet ---------- */}
      {showConfirm && (
        <div className="pkOverlay" onClick={() => !submitting && setShowConfirm(false)}>
          <div className="pkSheet" role="dialog" aria-modal="true" aria-labelledby="pkConfirmTitle" onClick={(e) => e.stopPropagation()}>
            <span className="pkHandle" aria-hidden="true" />
            <button
              type="button"
              className="pkClose"
              onClick={() => setShowConfirm(false)}
              disabled={submitting}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <h3 id="pkConfirmTitle">Confirm purchase</h3>
            <p className="pkSheetSub">Review the details before you pay</p>

            <div className="pkSheetAmount">
              <small>You pay</small>
              <b>{money(amount)}</b>
              <span>
                <TrendingUp size={13} /> Est. ROI {money(roiLow)} – {money(roiHigh)}
              </span>
            </div>

            <dl className="pkSummary">
              <div>
                <dt>Package</dt>
                <dd>{PACKAGE_NAME}</dd>
              </div>
              <div>
                <dt>ROI rate</dt>
                <dd>
                  {ROI_MIN}% – {ROI_MAX}%
                </dd>
              </div>
              <div>
                <dt>Pay from</dt>
                <dd>Fund Wallet</dd>
              </div>
              <div>
                <dt>Balance after</dt>
                <dd>{money(balanceAfter)}</dd>
              </div>
            </dl>

            <div className="pkSheetActions">
              <button type="button" className="pkBtn ghost" onClick={() => setShowConfirm(false)} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="pkBtn primary" onClick={confirmPurchase} disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="pkSpin" /> Processing…
                  </>
                ) : (
                  <>Confirm & pay {money(amount, 0)}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Packages;
